import { DelayedRender } from '@component/delayed_render.component';
import type { Signal } from '@preact/signals-react';
import { ReactNode } from 'react';

type AsyncSignalValueIdle = {
	value: null;
	flowState: 'idle';
};

type AsyncSignalValuePending = {
	value: null;
	flowState: 'pending';
};

type AsyncSignalValueError = {
	value: null;
	flowState: 'error';
};

type AsyncSignalValueAccessible<T> = {
	value: T;
	flowState: 'accessible';
};

export type AsyncSignalValue<T> =
	| AsyncSignalValueIdle
	| AsyncSignalValuePending
	| AsyncSignalValueError
	| AsyncSignalValueAccessible<T>;

interface Props<T> {
	derive: Signal<AsyncSignalValue<T>>;
	fallback?: ReactNode;
	catch?: ReactNode;
	children?: ((data: T) => ReactNode) | ReactNode;
	fallbackDelay?: number;
}

export function Async<T>({ fallbackDelay, derive, fallback, catch: $catch, children }: Props<T>): JSX.Element | null {
	if (derive.value.flowState === 'idle') {
		return null;
	}

	if (derive.value.flowState === 'pending') {
		return fallback ? <DelayedRender delay={fallbackDelay}>{fallback}</DelayedRender> : null;
	}

	if (derive.value.flowState === 'error') {
		return $catch ? <>{$catch}</> : null;
	}

	return <>{typeof children === 'function' ? children(derive.value.value) : children}</>;
}

export function isAsyncSignalAccessible<T>(value: Signal<AsyncSignalValue<T>>): value is Signal<AsyncSignalValueAccessible<T>> {
	return value.value.flowState === 'accessible';
}
